// Coalesce concurrent `video_analysis_data` (va-data) requests for the same file.
//
// The Inspection Insights page opens the analysis service AND the segmentation service at
// the same time, and both GET the identical va-data payload for the same file. Without
// coordination that (potentially large) JSON is fetched over the network and parsed twice
// on every page open.
//
// This coalesces overlapping GETs for the same file into a SINGLE network request: the
// first caller performs the fetch; concurrent callers await that same request and receive
// an independent deep copy. The copy matters — each MST service sorts parts of the payload
// in place (getSortedCodesByColor) and, in dev, deep-freezes it when assigning to the
// frozen `vaData` field; sharing one object between the two services would make the second
// sort a frozen array and throw. So we save the duplicate network + parse, while keeping
// each service's object independent.
//
// Only in-flight requests are shared — the entry is dropped as soon as the request settles,
// so later reloads / status polls always fetch fresh data (no staleness).

type ApiLike = { get: (endpoint: any) => Promise<any> };

const inFlightByKey = new Map<string, Promise<any>>();

function cloneVaData(data: any): any {
  const structuredCloneFn = (globalThis as any).structuredClone;
  if (typeof structuredCloneFn === "function") {
    try {
      return structuredCloneFn(data);
    } catch {
      // Fall through to a JSON clone if structuredClone chokes on an exotic value.
    }
  }
  return JSON.parse(JSON.stringify(data));
}

export function requestVaDataOnce(api: ApiLike, endpoint: any, fileId: number): Promise<any> {
  const key = `${fileId}|${endpoint?.queryParams ?? ""}`;
  const existing = inFlightByKey.get(key);
  if (existing) {
    // Subsequent concurrent caller: reuse the single fetch, but return an independent copy
    // so the two services never share (and mutate/deep-freeze) the same object.
    return existing.then((data) => cloneVaData(data));
  }

  const request = api.get(endpoint);
  // Drop the entry once settled (success or failure) so the next load fetches fresh.
  request.then(
    () => inFlightByKey.delete(key),
    () => inFlightByKey.delete(key)
  );
  inFlightByKey.set(key, request);
  return request; // first caller gets the original parsed object
}
